<!--
  LicenseDetail.svelte — one licence: what it grants, who holds its seats, and
  which machines are using them.

  Two things here write back to the product's licensing backend: releasing a seat
  a machine holds, and re-assigning seats to other people. The second is offered
  only when the backend says this licence may delegate seats — `canManageSeats`
  is its answer, not a guess made here — and the backend re-checks it on the write
  regardless of what this page rendered.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Badge,
		Checkbox,
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Spinner
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { ConfirmButton, EmptyState, Panel, RoutedLink, SeatAssignmentEditor, SeatMeter } from '../../components';
	import {
		getLicense,
		listLicenseActivations,
		deactivateLicenseActivations,
		updateLicenseSeats
	} from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import type { License, LicenseActivation, SeatAssignmentInput } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route params — which product and which licence. */
		params?: { appUUID?: string; licenseUUID?: string };
	}

	let { params = {} }: Props = $props();

	/** The licence being shown. */
	let license = $state<License | null>(null);
	/** Machines holding, or having held, seats of it. */
	let activations = $state<LicenseActivation[]>([]);
	/** Whether the licence is still loading. */
	let loading = $state(true);
	/** Failure of the licence read. */
	let error = $state('');
	/** Whether the activation list is still loading. */
	let loadingActivations = $state(false);
	/** Machines the user has ticked for release. */
	let selectedMachines = $state<string[]>([]);
	/** Whether a release is in flight. */
	let releasing = $state(false);

	/** Whether a seat save is in flight. */
	let savingSeats = $state(false);

	/** Load the licence and its machines. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** Read the licence, then its activations — the second needs the first to have succeeded. */
		try {
			license = await getLicense(params.appUUID as string, params.licenseUUID as string);
			await loadActivations();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load license');
		} finally {
			loading = false;
		}
	}

	/** Load the machines holding seats of this licence. */
	async function loadActivations(): Promise<void> {
		loadingActivations = true;
		/** A licence that reads fine but whose activations do not is still worth showing. */
		try {
			const page = await listLicenseActivations(params.appUUID as string, params.licenseUUID as string, {
				limit: 100
			});
			activations = page.items;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to load activations'));
		} finally {
			loadingActivations = false;
		}
	}

	onMount(load);

	/** Toggles one machine's selection for release. */
	function toggleMachine(activationUUID: string): void {
		selectedMachines = selectedMachines.includes(activationUUID)
			? selectedMachines.filter((uuid) => uuid !== activationUUID)
			: [...selectedMachines, activationUUID];
	}

	/** Releases every ticked machine's seat, then re-reads so the seat figures are the backend's. */
	async function handleRelease(): Promise<void> {
		releasing = true;
		/** Release the seats, then reload — the counts are recomputed server-side. */
		try {
			const revoked = await deactivateLicenseActivations(params.appUUID as string, selectedMachines);
			toastSuccess(t2('Released {{count}} seat(s).', { count: revoked }));
			selectedMachines = [];
			await load();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to release seats'));
		} finally {
			releasing = false;
		}
	}

	/**
	 * Saves the seat assignments the editor produced.
	 *
	 * The list replaces the licence's assignments outright — omitting one is how a
	 * block is withdrawn — and the backend re-checks both the budget and the
	 * caller's right to delegate before it applies anything.
	 */
	async function handleSaveSeats(assignments: SeatAssignmentInput[]): Promise<void> {
		savingSeats = true;
		/** Send the whole list; the endpoint replaces the set rather than patching it. */
		try {
			license = await updateLicenseSeats(params.appUUID as string, params.licenseUUID as string, assignments);
			toastSuccess(t2('Seat assignments saved.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save seat assignments'));
		} finally {
			savingSeats = false;
		}
	}

	/** Formats a date, or a dash when there is none. */
	function formatDate(date: string | null): string {
		if (!date) return '—';
		return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	}
</script>

<div class="space-y-6">
	<RoutedLink to="/Licenses/{params.appUUID}">
		<span class="text-sm text-gray-500 hover:underline dark:text-gray-400">
			<i class="fa-solid fa-arrow-left me-1"></i>{$t('Back to licenses')}
		</span>
	</RoutedLink>

	{#if loading}
		<EmptyState variant="loading" />
	{:else if error || !license}
		<EmptyState variant="error" description={error} />
	{:else}
		<AbstractCard class="max-w-none">
			<div class="flex flex-wrap items-start justify-between gap-4">
				<div class="min-w-0">
					<h3 class="text-xl font-semibold text-gray-900 dark:text-white">
						{license.projectName || license.licenseUUID}
					</h3>
					<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
						{$t('Valid')} {formatDate(license.startDate)} — {formatDate(license.endDate)}
					</p>
				</div>
				<div class="flex items-center gap-2">
					{#if license.isPaused}
						<Badge color="yellow">{$t('Paused')}</Badge>
					{:else if license.isActive}
						<Badge color="green">{$t('Active')}</Badge>
					{:else}
						<Badge color="red">{$t('Expired')}</Badge>
					{/if}
					{#if license.isSublicense}
						<Badge color="purple">{$t('Assigned seat')}</Badge>
					{/if}
				</div>
			</div>

			{#if license.isPaused && license.pausedReason}
				<p class="mt-3 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
					<i class="fa-solid fa-circle-info me-2"></i>{license.pausedReason}
				</p>
			{/if}

			<SeatMeter class="mt-5" used={license.seats.used} total={license.seats.total} />

			{#if license.entitlements.length > 0}
				<div class="mt-5">
					<p class="card-label">{$t('Entitlements')}</p>
					<div class="mt-2 flex flex-wrap gap-1">
						{#each license.entitlements as entitlement (entitlement.entitlementUUID ?? entitlement.name)}
							<Badge
								color="blue"
								title={entitlement.description ?? undefined}
								class={entitlement.description ? 'cursor-help' : ''}
							>
								{entitlement.name}
							</Badge>
						{/each}
					</div>
				</div>
			{/if}
		</AbstractCard>

		{#if license.capabilities.canManageSeats}
			<Panel title={$t('Seat assignments')} faIcon="user-group">
				<p class="text-sm text-gray-500 dark:text-gray-400">
					{$t('Give blocks of seats to colleagues. They activate their own machines against the seats you assign; removing a block takes the seats back.')}
				</p>

				<div class="mt-4">
					<SeatAssignmentEditor {license} saving={savingSeats} onsave={handleSaveSeats} />
				</div>
			</Panel>
		{/if}

		<Panel title={$t('Machines')} faIcon="desktop">
			<div class="mb-3 flex flex-wrap items-center justify-between gap-2">
				<p class="text-sm text-gray-500 dark:text-gray-400">
					{$t('Machines currently using a seat of this license.')}
				</p>
				{#if selectedMachines.length > 0}
					<ConfirmButton size="sm" color="red" disabled={releasing} onconfirm={handleRelease}>
						{#if releasing}<Spinner size="4" class="me-2" />{/if}
						{$t('Release {{count}} seat(s)', { count: selectedMachines.length })}
					</ConfirmButton>
				{/if}
			</div>

			{#if loadingActivations}
				<EmptyState variant="loading" compact />
			{:else if activations.length === 0}
				<!-- Outside the table on purpose: an empty state inside a `colspan` row
				     leaves the browser sizing columns from a header band alone, which is
				     how a header ends up with its later cells squeezed to nothing. -->
				<EmptyState
					faIcon="desktop"
					title={$t('No machines')}
					description={$t('No machine has activated this license yet.')}
				/>
			{:else}
				<Table divClass="widget !p-1 relative" class="dataTable">
					<TableHead>
						<TableHeadCell class="w-10"><span class="sr-only">{$t('Select')}</span></TableHeadCell>
						<TableHeadCell>{$t('Machine')}</TableHeadCell>
						<TableHeadCell>{$t('IP Address')}</TableHeadCell>
						<TableHeadCell>{$t('Activated')}</TableHeadCell>
						<TableHeadCell>{$t('Status')}</TableHeadCell>
					</TableHead>
					<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
						{#each activations as activation (activation.activationUUID)}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell>
									{#if activation.isActive && license.capabilities.canDeactivateActivations}
										<Checkbox
											checked={selectedMachines.includes(activation.activationUUID)}
											onchange={() => toggleMachine(activation.activationUUID)}
											aria-label={$t('Select {{machine}}', { machine: activation.machine })}
										/>
									{/if}
								</TableBodyCell>
								<TableBodyCell>
									<span class="font-medium">{activation.machine || '—'}</span>
									{#if activation.notes}
										<p class="text-xs text-gray-500 dark:text-gray-400">{activation.notes}</p>
									{/if}
								</TableBodyCell>
								<TableBodyCell>{activation.machineIP || '—'}</TableBodyCell>
								<TableBodyCell class="!whitespace-nowrap">{formatDate(activation.activationDate)}</TableBodyCell>
								<TableBodyCell>
									{#if activation.isActive}
										<Badge color="green">{$t('Active')}</Badge>
									{:else}
										<Badge color="gray">{$t('Released')}</Badge>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>
	{/if}
</div>
